// Categories.jsx — the top level of the catalogue.
//
// ── ONE THING WORTH KNOWING BEFORE EDITING ──
// The body sent to this API is JSON, never FormData: a multipart body is a 415
// against this service. The picture is uploaded separately through
// /admin/media/categories, which answers with a relative uploads path, and
// that path is what goes in the body. Leaving `image` out of the body entirely
// is how you keep the existing picture — sending an empty string clears it.

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { uploadImage } from "../../../api/media";
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  toggleCategoryStatus,
} from "../../../slice/category/categorySlice";
import BannerCropModal from "../banner/BannerCropModal";
import StoreScopePicker, {
  defaultStoreScope,
  storeScopeOf,
} from "../component/StoreScopePicker";
import {
  PageShell,
  DataTable,
  Button,
  Thumb,
  Toggle,
  RowActions,
  Modal,
  Field,
  ImagePicker,
  Pager,
  PlusIcon,
} from "../component/AdminUi";

const BASE_URL = process.env.REACT_APP_API_URL;
const PER_PAGE = 10;

/* The catalogue stores a path, not a URL, and the API returns it as imageUrl.
   `image` is read too because a couple of endpoints still answer that
   spelling, and an already-absolute URL is left alone — the same three rules
   the app's ApiConfig.imageUrl follows, so the two agree. */
const pictureOf = (row) => {
  const raw = (row?.imageUrl || row?.image || "").trim();
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `${BASE_URL}/uploads/${raw.replace(/^\/+/, "").replace(/^uploads\//, "")}`;
};

const onDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function Categories() {
  const dispatch = useDispatch();
  const { categories, loading, error, pagination } = useSelector(
    (s) => s.category
  );

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState(defaultStoreScope());
  const [picked, setPicked] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cropping, setCropping] = useState(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    dispatch(fetchCategories({ page, limit: PER_PAGE, search: query }));
  }, [dispatch, page, query]);

  /* ── the form ──────────────────────────────────────────────────────────── */

  const openNew = () => {
    setEditing(null);
    setName("");
    setScope(defaultStoreScope());
    setPicked(null);
    setPreview(null);
    setProblem("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setName(row.name || "");
    setScope(storeScopeOf(row));
    /* The same reader the table uses. This once looked for row.image — which
       the API does not send — so opening a category to rename it showed an
       empty picture box, and saving with no new file appeared to wipe the
       image even though the server had kept it. */
    setPreview(pictureOf(row) || null);
    setPicked(null);
    setProblem("");
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    setName("");
    setScope(defaultStoreScope());
    setPicked(null);
    setPreview(null);
    setProblem("");
  };

  /* Tiles show these square, so the crop is fixed 1:1 rather than left to
     whatever shape the file happened to be. */
  const cropped = (file) => {
    setPicked(file);
    setPreview(URL.createObjectURL(file));
    setCropping(null);
  };

  const save = async () => {
    if (!name.trim()) return setProblem("Give the category a name.");
    if (!scope.availableInAllStores && scope.stores.length === 0)
      return setProblem("Choose at least one store, or make it estate-wide.");

    setSaving(true);
    setProblem("");

    const body = { name: name.trim() };

    try {
      if (picked) body.image = await uploadImage(picked, "categories");

      if (editing) {
        await dispatch(
          updateCategory({ id: editing.id, formData: body })
        ).unwrap();
        toast.success("Category updated");
      } else {
        await dispatch(createCategory(body)).unwrap();
        setPage(1);
        toast.success("Category added");
      }

      dispatch(
        fetchCategories({
          page: editing ? page : 1,
          limit: PER_PAGE,
          search: query,
        })
      );
      close();
    } catch (err) {
      const message = err || "That did not save.";
      setProblem(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (
      !window.confirm(
        `Delete "${row.name}"? Products in it keep their own records.`
      )
    )
      return;
    try {
      await dispatch(deleteCategory(row.id)).unwrap();
      dispatch(fetchCategories({ page, limit: PER_PAGE, search: query }));
      toast.success("Category deleted");
    } catch (err) {
      toast.error(err || "Could not delete it.");
    }
  };

  const rows = categories || [];

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <PageShell
      title="Categories"
      count={
        loading && !rows.length
          ? "Loading…"
          : pagination?.total != null
          ? `${pagination.total} in the catalogue`
          : `${rows.length} shown`
      }
      search={{
        value: query,
        onChange: (v) => {
          setQuery(v);
          setPage(1);
        },
        placeholder: "Search categories",
      }}
      actions={
        <Button kind="primary" onClick={openNew}>
          <PlusIcon />
          Add category
        </Button>
      }
    >
      {cropping ? (
        <BannerCropModal
          file={cropping}
          aspect={1}
          onCancel={() => setCropping(null)}
          onCropped={cropped}
        />
      ) : null}

      <DataTable
        columns={["", "Name", "Shown", "Added", ""]}
        loading={loading}
        error={error}
        isEmpty={!loading && rows.length === 0}
        emptyTitle={query ? "No category matches that" : "No categories yet"}
        emptyHint={
          query
            ? "Try a shorter search."
            : "Categories are the top level of the catalogue — add the first one."
        }
      >
        {rows.map((row) => (
          <tr key={row.id}>
            <td style={{ width: 62 }}>
              <Thumb src={pictureOf(row)} alt={row.name} />
            </td>
            <td>
              <span className="ui-who__name">{row.name}</span>
            </td>
            <td>
              <Toggle
                on={row.status !== false}
                onChange={() => dispatch(toggleCategoryStatus(row.id))}
                label={
                  row.status !== false
                    ? "Shown in the app — tap to hide"
                    : "Hidden — tap to show"
                }
              />
            </td>
            <td className="ui-num">{onDate(row.createdAt)}</td>
            <td>
              <RowActions
                onEdit={() => openEdit(row)}
                onDelete={() => remove(row)}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <Pager
        page={page}
        totalPages={pagination?.totalPages}
        total={pagination?.total}
        perPage={PER_PAGE}
        onPage={setPage}
      />

      <Modal
        open={open}
        title={editing ? `Edit ${editing.name}` : "Add category"}
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button kind="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Add it"}
            </Button>
          </>
        }
      >
        {problem ? <div className="ui-alert ui-alert--error">{problem}</div> : null}

        <Field label="Name" required>
          <input
            className="ui-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Flours"
          />
        </Field>

        <Field
          label="Picture"
          hint="Cropped square, because that is how the app lays categories out."
        >
          <ImagePicker
            preview={preview}
            onPick={(file) => setCropping(file)}
            hint="A clear photograph of what is in the category"
          />
        </Field>

        <Field label="Stores">
          <StoreScopePicker value={scope} onChange={setScope} />
        </Field>
      </Modal>
    </PageShell>
  );
}
